import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, FolderKanban, EyeOff, Eye, LayoutTemplate } from "lucide-react";
import { api } from "@/lib/api";
import { PROJECT_STATUSES } from "@/lib/taskConstants";
import { CreateProjectDialog } from "@/components/project/CreateProjectDialog";
import { TemplatesGalleryDialog } from "@/components/project/TemplatesGalleryDialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export const ProjectsSection = ({ workspaceId, canCreate }) => {
  const [createOpen, setCreateOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [hideDone, setHideDone] = useState(() => localStorage.getItem("atlas_hide_done_projects") === "1");
  const { data: allProjects = [] } = useQuery({
    queryKey: ["projects", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}/projects`)).data,
  });
  const hiddenCount = allProjects.filter((p) => p.status === "completed").length;
  const projects = hideDone ? allProjects.filter((p) => p.status !== "completed") : allProjects;
  const toggleHide = () => {
    const next = !hideDone;
    setHideDone(next);
    localStorage.setItem("atlas_hide_done_projects", next ? "1" : "0");
  };

  return (
    <Card className="p-6 space-y-4" data-testid="space-projects-card">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Proyectos</h2>
        <div className="flex items-center gap-1">
          {hiddenCount > 0 && (
            <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={toggleHide}
              data-testid="space-hide-done-projects-btn">
              {hideDone ? <Eye className="h-3.5 w-3.5 mr-1" /> : <EyeOff className="h-3.5 w-3.5 mr-1" />}
              {hideDone ? `Mostrar finalizados (${hiddenCount})` : "Ocultar finalizados"}
            </Button>
          )}
          {canCreate && (
            <>
              <Button variant="ghost" size="sm" data-testid="space-templates-btn" onClick={() => setTemplatesOpen(true)}>
                <LayoutTemplate className="h-4 w-4 mr-1" /> Plantillas
              </Button>
              <Button variant="ghost" size="sm" data-testid="space-create-project-btn" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-1" /> Nuevo
              </Button>
            </>
          )}
        </div>
      </div>
      {projects.length === 0 ? (
        <div className="text-center py-6">
          <FolderKanban className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">Sin proyectos todavía. Un proyecto agrupa tareas con un mismo objetivo.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {projects.map((p) => {
            const pct = p.task_count > 0 ? Math.round((p.done_count / p.task_count) * 100) : 0;
            const status = PROJECT_STATUSES.find((s) => s.value === p.status);
            return (
              <Link key={p.id} to={`/space/${workspaceId}/project/${p.id}`} data-testid={`project-card-${p.id}`}>
                <Card className="p-4 hover:border-primary/50 transition-colors space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg flex items-center justify-center text-lg shrink-0"
                      style={{ backgroundColor: `${p.color}1f`, border: `1px solid ${p.color}40` }}>
                      {p.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-heading font-semibold text-sm truncate">{p.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {p.task_count} {p.task_count === 1 ? "tarea" : "tareas"}
                      </p>
                    </div>
                    <Badge variant="secondary" className="text-[10px] shrink-0" style={{ color: status?.color }}>
                      {status?.label}
                    </Badge>
                  </div>
                  <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: p.color }} />
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
      <CreateProjectDialog workspaceId={workspaceId} open={createOpen} onOpenChange={setCreateOpen} />
      <TemplatesGalleryDialog workspaceId={workspaceId} open={templatesOpen} onOpenChange={setTemplatesOpen} />
    </Card>
  );
};
